/**
 * purchaseRestrictionCopy.js — FE MIRROR of functions/utils/purchaseRestrictionPolicy.js.
 *
 * 🚨 GENERATED. Do not edit by hand.
 *   Regenerate:  cd functions && node scripts/_generatePurchaseRestrictionCopy.js
 *   Verify:      cd functions && node utils/_testPurchaseRestrictions.js   (§13 diffs the pair)
 *
 * This file exists only because Create React App cannot import from outside src/, so the words
 * shown on a lead page have to be duplicated. §13 reads this file, evaluates it, and fails on any
 * divergence from the backend — both the copy table AND the date helpers.
 *
 * Only display concerns live here. Every judgement — which terms can archive a lead, how a date is
 * read out of a quote, which combination is permanent — stays on the backend, where it is tested.
 * The frontend renders words and asks whether a block is still live; it never decides anything.
 */

// Terms that are true but not worth opening a panel for on their own. `as_is` is on 10 of 132
// leads and is simply the norm for distressed inventory.
export const NON_ALERTING_TERMS = new Set(["as_is", "other"]);

export const PURCHASE_RESTRICTION_COPY = {
    first_look_owner_occupant: {
        enforceability: "binding",
        title: "You can't make an offer yet",
        body: "This home is owned by a bank or government housing agency. For the first few weeks they only accept offers from people who will actually live in it, so investors are shut out until that window closes.",
        whatToDo: "Put the date in your diary. Every other investor watching this listing can see the same date, so be ready to move that morning rather than that week.",
    },
    buyer_program_eligibility: {
        enforceability: "binding",
        title: "The advertised price isn't available to you",
        body: "That price comes with a special loan or a grant that only certain buyers qualify for, usually first-time buyers. You can still buy the house — just not at the price in the advert.",
        whatToDo: "Ask the agent what an investor would actually pay, and work from that number instead. The advertised one will make the deal look better than it is.",
    },
    age_restricted_community: {
        enforceability: "binding",
        title: "Only older residents can live here",
        body: "You are allowed to buy it, but everyone who lives there has to meet the community's age rule — and that includes any tenant you put in. It rules out sharing the house between several tenants.",
        whatToDo: "Read the community rules before you count on rental income. Some also cap how many homes can be let at once.",
    },
    deed_restriction_or_income_cap: {
        enforceability: "binding",
        title: "Investors are not allowed to own this one",
        body: "There is a legal restriction written into the deed that limits who may own the home and what it can be resold for. It is usually affordable housing, and it stays with the property whoever buys it.",
        whatToDo: null,
    },
    co_op_board_approval: {
        enforceability: "binding",
        title: "A board decides who is allowed to buy",
        body: "This is a co-op, so you would be buying shares in the building rather than the home itself. A board has to approve you, they do not have to give a reason, and most of them ban investors and renting out entirely.",
        whatToDo: null,
    },
    short_sale_lender_approval: {
        enforceability: "binding",
        title: "The seller's bank has the final say",
        body: "The owner owes more than the home is selling for, so their bank has to approve the sale. The seller cannot agree a price on their own, and the bank will not pay for repairs or give money off.",
        whatToDo: "Ask two things. First, has the bank already approved a price? If yes this can close in weeks; if not, expect months. Second, what does their approval letter say about reselling — these often block you from selling it on for 30 to 90 days, which kills a quick flip.",
    },
    court_confirmation_required: {
        enforceability: "binding",
        title: "A judge has to approve this sale",
        body: "A court is overseeing the sale, so your offer being accepted does not mean the house is yours. It has to be confirmed at a hearing, and in many places other buyers can turn up and bid against you at that hearing.",
        whatToDo: "Allow an extra 30 to 45 days, and hold off on expensive inspections until you know you are not going to be outbid at the last moment.",
    },
    bankruptcy_or_receiver: {
        enforceability: "binding",
        title: "A court-appointed trustee has to approve",
        body: "The owner is in bankruptcy, so a trustee appointed by the court controls the sale. These take a couple of months and the property can be offered to higher bidders right up to the hearing.",
        whatToDo: "Treat the timeline as out of your hands, and do not line up a buyer or a loan that expires.",
    },
    estate_or_probate: {
        enforceability: "binding",
        title: "The owner has died",
        body: "The family or the person handling the estate is selling. It is almost always sold exactly as it stands, and it can move slowly because several people have to agree.",
        whatToDo: "Ask whether a court also has to sign off — that adds another month or more.",
    },
    auction_bidding: {
        enforceability: "binding",
        title: "This one sells by auction",
        body: "The price shown is a starting point, not the price. It goes to the highest bidder, there is often a fee added on top of whatever you bid, and you usually cannot pull out or get your deposit back.",
        whatToDo: "Work out your maximum including the fee before you bid, and assume you cannot inspect it first.",
    },
    right_of_first_refusal: {
        enforceability: "binding",
        title: "Someone else can step in and match your offer",
        body: "The tenant, the homeowners association or the local authority has the right to buy it on the same terms you agree. You can do all the work of negotiating and still lose it.",
        whatToDo: "Find out who holds that right and how long they have to decide before you spend anything.",
    },
    cash_or_hard_money_only: {
        enforceability: "binding",
        title: "A normal mortgage will not work here",
        body: "Something about the home — usually its condition, or that the power and water cannot be turned on — means an ordinary lender will not lend on it. You need cash or a short-term lender.",
        whatToDo: "Remember this applies to whoever buys it from you as well, until the work is done. That shrinks the pool of people you can sell to.",
    },
    mandated_lender: {
        enforceability: "preference",
        title: "The seller wants you to use their lender",
        body: "That is their preference, not the law, and it does not apply at all if you are paying cash. It is worth asking why they have one — it usually means ordinary lenders will not lend on this kind of property.",
        whatToDo: "You can push back on it. But do find out what those lenders charge, because it tells you what your own buyer will face later.",
    },
    mandated_title_company: {
        enforceability: "preference",
        title: "The seller wants to choose the closing company",
        body: "You do not have to agree. A seller is not allowed to make you buy title insurance from a particular company as a condition of selling you the house.",
        whatToDo: "Use your own if you prefer. If they are paying for it, letting them choose is usually fine.",
    },
    no_assignment_or_seasoning: {
        enforceability: "binding",
        title: "You cannot pass this on or sell it quickly",
        body: "The contract stops you handing your purchase to another buyer, and often stops you reselling for a set period after you complete. Banks add these specifically to keep quick resellers out.",
        whatToDo: "If the plan was to flip the contract to someone else, that will not work here. You have to actually buy it.",
    },
    seller_financing_offered: {
        enforceability: "preference",
        title: "The seller may help with the money",
        body: "They have mentioned rent-to-own, or lending you part of the price themselves. That is unusual and it is in your favour — it can make a deal work even when the cash price does not.",
        whatToDo: "Worth a phone call even if the numbers look tight on paper.",
    },
    tenant_occupied_or_lease: {
        enforceability: "binding",
        title: "Someone already lives here",
        body: "The tenant and their agreement come with the house. Until that agreement ends you cannot renovate it, change the rent, or move anyone else in — so you cannot convert it or do it up to sell.",
        whatToDo: "Ask for the agreement and the rent before you offer. A good long-term tenant is worth having; a cheap one you cannot remove is not.",
    },
    land_lease_or_lot_rent: {
        enforceability: "binding",
        title: "You would own the house but not the land",
        body: "The land underneath belongs to someone else and you pay them ground rent every month, which they can put up. The land never gains value for you, and ordinary mortgages are usually not available on these.",
        whatToDo: "Find out the ground rent and how often it rises, then take it straight off your monthly income before you judge the deal.",
    },
    solar_or_pace_obligation: {
        enforceability: "binding",
        title: "The solar panels come with a bill attached",
        body: "The panels are not paid off. You would take over the payments, and the solar company has to approve you separately from your mortgage. If they say no, the sale falls through.",
        whatToDo: "Get the solar agreement before you make an offer, not during checks. Whoever buys it from you will have to be approved too.",
    },
    hoa_rental_limit: {
        enforceability: "binding",
        title: "The homeowners association limits renting",
        body: "There is a cap on how many homes may be rented out, or a minimum lease length. If the cap is already full you could own a house you are not allowed to rent.",
        whatToDo: "Call the association and ask whether the cap is full and how long the waiting list is. Do this before you offer, not after.",
    },
    tax_sale_redemption_or_title_defect: {
        enforceability: "binding",
        title: "The ownership is not clean yet",
        body: "This came out of a tax sale, so the old owner may still have a legal right to take it back. No insurer will cover the title until that is settled, and settling it means a lawyer and a court case rather than a form.",
        whatToDo: "Ask which state rule applies. In Georgia you hold a deed the old owner can buy back for 12 months. In Arizona you may hold only a certificate, with a three-year wait before you can start taking ownership. Those are not the same purchase.",
    },
    land_bank_or_public_program: {
        enforceability: "binding",
        title: "You have to apply for this one",
        body: "This is sold by a land bank or a public housing program, not on the open market. They choose the buyer, and they usually attach rules to the deed about what you have to build and how quickly.",
        whatToDo: "Ask what conditions come with it. A deadline to finish repairs is workable. A rule keeping the rent low for twenty years is not an investment.",
    },
    no_interior_access: {
        enforceability: "binding",
        title: "Getting inside is difficult",
        body: "There is no key box, or no interior viewing at all. Every visit has to go through one person, which slows down inspections and builders' quotes — or means you are buying without having seen inside.",
        whatToDo: "Build extra time in. If nobody can get in at all, treat our repair estimate as a rough guess rather than a number.",
    },
    no_blind_offers: {
        enforceability: "preference",
        title: "The listing says no blind offers",
        body: "The agent wants buyers to see the house before offering. Agents have to pass on every offer they are given, so you can still make one. Sellers ask for this because deals from buyers who had not seen the house have often fallen apart later.",
        whatToDo: "Expect a cooler reception if you skip the visit, and expect them to resist you passing the contract on to someone else later.",
    },
    offer_process_preference: {
        enforceability: "preference",
        title: "The agent has set terms for making an offer",
        body: "The listing asks for something up front, such as proof of funds before a showing or a deadline for offers. These are the agent's terms, not the law.",
        whatToDo: "Have your proof of funds ready and put any deadline in your diary. Going against them tends to get you passed over.",
    },
    seller_is_agent: {
        enforceability: "preference",
        title: "The agent is connected to the seller",
        body: "Properly disclosed and usually nothing to worry about. It only matters on distressed sales like short sales, where everyone signs a form confirming the buyer and seller are not connected.",
        whatToDo: "Nothing, unless this is also a short sale or a repossession.",
    },
    permit_or_zoning_unapproved: {
        enforceability: "binding",
        title: "The extra potential has not been approved",
        body: "The listing talks about what the property could become — more units, an extension, a change of use — but the city has not agreed to any of it. It is a maybe, not a fact, and it may never happen.",
        whatToDo: "Value it on what it is today and treat the extra as a free bonus if it comes off.",
    },
    unpermitted_work_done: {
        enforceability: "binding",
        title: "Work has already been done without permission",
        body: "Someone has carried out work on this house and never got the city to sign it off. The city can make you put that right afterwards, and an inspector may need walls opened up to see what is behind them.",
        whatToDo: "Get a price for putting the paperwork right before you offer, and assume some finished work may have to come out. Space without permission may also not count when the house is valued.",
    },
    as_is: {
        enforceability: "binding",
        title: "Sold exactly as it stands",
        body: "The seller will not carry out any repairs or knock money off for problems you find. Normal for this kind of property, but it means your repair budget has no room in it.",
        whatToDo: null,
    },
    other: {
        enforceability: "preference",
        title: "Something unusual about this sale",
        body: "The listing mentions a condition of sale that does not fit the usual categories. The exact wording is below.",
        whatToDo: "Read the quote and ask the agent if it is not clear.",
    },
};

// ─── READING A STORED DATE BACK ───────────────────────────────────────────────────────────
// Mirrors functions/utils/purchaseRestrictionPolicy.js. `blockedUntil` is frozen at ingestion;
// whether it still binds is a question about TODAY and must be answered on every render.
// String comparison, not Date arithmetic: both sides are ISO `YYYY-MM-DD`, which sorts
// lexicographically, so this needs no parsing and cannot drift by a timezone. `new Date(iso)`
// parses as UTC midnight and renders in the local zone, printing the day BEFORE anywhere west
// of Greenwich — a day early, in the one direction that costs a deal.

/** Today as an ISO `YYYY-MM-DD`, in UTC. */
export function todayISO(now = new Date()) {
    const d = now instanceof Date ? now : new Date(now);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

/** Is a stored `blockedUntil` still in force? Inclusive of the named day — fails long, never short. */
export function isBlockStillActive(blockedUntil, now = new Date()) {
    if (!blockedUntil || typeof blockedUntil !== 'string') return false;
    return blockedUntil >= todayISO(now);
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

/** Format an ISO `YYYY-MM-DD` from its own parts — never via a parsed Date. */
export function formatBlockDate(isoDate) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(isoDate || ''));
    if (!m) return isoDate == null ? null : String(isoDate);
    const month = MONTH_NAMES[Number(m[2]) - 1];
    if (!month) return String(isoDate);
    return `${month} ${Number(m[3])}, ${m[1]}`;
}

/** The words for one term, or null for an unknown one. Never throws. */
export function describeTerm(term) {
    const p = PURCHASE_RESTRICTION_COPY[term];
    if (!p) return null;
    return { term, ...p };
}

/**
 * Turn a stored `purchaseRestrictions.summary` into rows for the lead page.
 * Returns [] when the pass never ran, when nothing was found, or when the only findings are
 * non-alerting — so a caller can render on truthiness alone.
 */
/**
 * 🚨 THE ONE ANSWER TO "WHAT DO WE SHOW, AND IN WHAT ORDER?" — mirror of
 * purchaseRestrictionPolicy.visibleRestrictions, pinned behaviourally by §13.
 *
 * It was three implementations for one day and they disagreed immediately: MCP let a term the
 * copy table did not know vote for "something real is here", then skipped it at render, leaving
 * `as_is` alone; and un-suppressing `as_is` let it render first, burying the finding it merely
 * qualifies. Both were one display decision made in more than one place.
 */
export function visibleRestrictions(summary, now = new Date()) {
    if (!summary || !Array.isArray(summary.terms)) return [];
    const stillBlocked = isBlockStillActive(summary.blockedUntil, now);

    const live = summary.terms
        // No copy ⇒ cannot be rendered ⇒ must not get a vote on whether anything IS rendered.
        // Stored docs outlive the vocabulary.
        .filter((t) => t && PURCHASE_RESTRICTION_COPY[t.term])
        // An expired time-limited term has nothing left to say: its entire content was the
        // deadline. Keeping it would tell the operator they cannot offer on a property that
        // has been open to them for weeks.
        .filter((t) => !(t.blocking === "until_date" && !stillBlocked));

    // `as_is` alone is not news — it is the norm for distressed inventory, and a panel that fires
    // on a third of the book stops being read. Beside a real restriction it IS worth reading:
    // "sold as-is AND work already done without permission" is one picture, not two facts.
    // ⚠️ Checked AFTER the expiry filter. A lead whose only real finding was a First Look that has
    // since lapsed would otherwise open the panel to show nothing but as-is.
    if (!live.some((t) => !NON_ALERTING_TERMS.has(t.term))) return [];

    // Boilerplate trails the finding it qualifies. `sort` is stable, so equal ranks keep order.
    return [...live].sort((a, b) =>
        (NON_ALERTING_TERMS.has(a.term) ? 1 : 0) - (NON_ALERTING_TERMS.has(b.term) ? 1 : 0));
}

export function describePurchaseRestrictions(summary, now = new Date()) {
    return visibleRestrictions(summary, now)
        .map((t) => ({
            ...describeTerm(t.term),
            quote: t.quote || null,
            blockedUntil: t.blockedUntil || null,
            isPreference: PURCHASE_RESTRICTION_COPY[t.term].enforceability === "preference",
        }));
}
